// Weather service for Northeast India districts
// Uses OpenWeatherMap API (free tier: 1000 calls/day)

const OPENWEATHER_API_KEY = process.env.REACT_APP_OPENWEATHER_API_KEY || 'demo'; // User will add their key

// Major district headquarters for weather monitoring
export const weatherStations = [
  // Nagaland
  { name: 'Kohima', state: 'Nagaland', coordinates: [25.6747, 94.1077] },
  { name: 'Dimapur', state: 'Nagaland', coordinates: [25.9115, 93.7283] },
  { name: 'Mokokchung', state: 'Nagaland', coordinates: [26.3228, 94.5247] },
  { name: 'Mon', state: 'Nagaland', coordinates: [26.7167, 95.0000] },
  
  // Assam
  { name: 'Guwahati', state: 'Assam', coordinates: [26.1445, 91.7362] },
  { name: 'Jorhat', state: 'Assam', coordinates: [26.7509, 94.2037] },
  { name: 'Silchar', state: 'Assam', coordinates: [24.8333, 92.7789] },
  { name: 'Dibrugarh', state: 'Assam', coordinates: [27.4728, 94.9120] },
  
  // Manipur
  { name: 'Imphal', state: 'Manipur', coordinates: [24.8170, 93.9368] },
  { name: 'Churachandpur', state: 'Manipur', coordinates: [24.3333, 93.6805] },
  
  // Arunachal Pradesh
  { name: 'Itanagar', state: 'Arunachal Pradesh', coordinates: [27.0844, 93.6053] },
  { name: 'Tawang', state: 'Arunachal Pradesh', coordinates: [27.5860, 91.8577] },
  { name: 'Pasighat', state: 'Arunachal Pradesh', coordinates: [28.0666, 95.3269] },
  
  // Tripura
  { name: 'Agartala', state: 'Tripura', coordinates: [23.8315, 91.2868] },
  
  // Mizoram
  { name: 'Aizawl', state: 'Mizoram', coordinates: [23.7307, 92.7173] },
  { name: 'Lunglei', state: 'Mizoram', coordinates: [22.8846, 92.7392] },
  
  // Sikkim
  { name: 'Gangtok', state: 'Sikkim', coordinates: [27.3389, 88.6065] },
  
  // Meghalaya
  { name: 'Shillong', state: 'Meghalaya', coordinates: [25.5788, 91.8933] },
  { name: 'Tura', state: 'Meghalaya', coordinates: [25.5138, 90.2022] }
];

// Fetch current weather for a location
export const fetchWeather = async (lat, lon) => {
  try {
    const url = `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lon}&appid=${OPENWEATHER_API_KEY}&units=metric`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error('Weather data unavailable');
    }
    
    const data = await response.json();
    return {
      temperature: Math.round(data.main.temp),
      feelsLike: Math.round(data.main.feels_like),
      humidity: data.main.humidity,
      pressure: data.main.pressure,
      windSpeed: data.wind.speed,
      windDirection: data.wind.deg,
      description: data.weather[0].description,
      main: data.weather[0].main,
      icon: data.weather[0].icon,
      visibility: data.visibility / 1000, // Convert to km
      rain: data.rain ? data.rain['1h'] || 0 : 0,
      clouds: data.clouds.all
    };
  } catch (error) {
    console.error('Error fetching weather:', error);
    return null;
  }
};

// Fetch weather for all stations
export const fetchAllStationWeather = async () => {
  const weatherData = [];
  
  for (const station of weatherStations) {
    const weather = await fetchWeather(station.coordinates[0], station.coordinates[1]);
    if (weather) {
      weatherData.push({
        ...station,
        weather
      });
    }
    // Add small delay to respect API rate limits
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  
  return weatherData;
};

// Determine if weather is severe
export const isWeatherSevere = (weather) => {
  if (!weather) return false;
  
  const { main, rain, windSpeed, visibility } = weather;
  
  // Severe conditions
  if (main === 'Thunderstorm' || main === 'Tornado') return true;
  if (rain > 7.5) return true; // Heavy rain (>7.5mm/hr)
  if (windSpeed > 10) return true; // Strong wind (>36 km/h)
  if (visibility < 1) return true; // Poor visibility (<1km)
  
  return false;
};

// Determine weather severity level
export const getWeatherSeverity = (weather) => {
  if (!weather) return 'normal';
  
  const { main, rain, windSpeed } = weather;
  
  if (main === 'Thunderstorm' || rain > 10 || windSpeed > 15) {
    return 'severe'; // Red
  }
  
  if (main === 'Rain' || rain > 2.5 || windSpeed > 7) {
    return 'moderate'; // Orange
  }
  
  if (main === 'Drizzle' || main === 'Clouds') {
    return 'mild'; // Yellow
  }
  
  return 'normal'; // Green
};

// Check if currently in monsoon season
export const isMonsoonSeason = () => {
  const month = new Date().getMonth() + 1; // 1-12
  return month >= 6 && month <= 9; // June to September
};

// Get weather color for map overlay
export const getWeatherColor = (severity) => {
  switch (severity) {
    case 'severe':
      return '#DC2626'; // Red
    case 'moderate':
      return '#F59E0B'; // Orange
    case 'mild':
      return '#FCD34D'; // Yellow
    default:
      return '#4A7C59'; // Green
  }
};

// Generate weather alert message
export const generateWeatherAlert = (weather, location) => {
  if (!weather) return null;
  
  const { main, rain, windSpeed, temperature } = weather;
  const alerts = [];
  
  if (main === 'Thunderstorm') {
    alerts.push(`⚠️ Thunderstorm in ${location} - Avoid travel if possible`);
  }
  
  if (rain > 7.5) {
    alerts.push(`🌧️ Heavy rainfall (${rain}mm/hr) in ${location} - Road conditions may deteriorate`);
  }
  
  if (windSpeed > 10) {
    alerts.push(`💨 Strong winds (${Math.round(windSpeed * 3.6)}km/h) in ${location} - Drive carefully`);
  }
  
  if (temperature < 5) {
    alerts.push(`❄️ Cold conditions (${temperature}°C) in ${location} - Watch for ice on roads`);
  }
  
  return alerts.length > 0 ? alerts : null;
};

// Mock weather data for demo (when API key not available)
export const mockWeatherData = weatherStations.map(station => ({
  ...station,
  weather: {
    temperature: Math.floor(Math.random() * 15) + 15, // 15-30°C
    humidity: Math.floor(Math.random() * 40) + 60, // 60-100%
    windSpeed: Math.floor(Math.random() * 10) + 2, // 2-12 m/s
    description: ['clear sky', 'few clouds', 'scattered clouds', 'broken clouds', 'light rain'][Math.floor(Math.random() * 5)],
    main: ['Clear', 'Clouds', 'Clouds', 'Clouds', 'Rain'][Math.floor(Math.random() * 5)],
    rain: Math.random() > 0.7 ? Math.random() * 10 : 0,
    visibility: 10,
    icon: '01d'
  }
}));